import { pb } from "@/lib/core/pocketbase";

/**
 * The sign-up proof of work (server/pb_hooks/abuse.pb.js, the ALTCHA scheme): find the number n
 * in [0, maxNumber] with sha256(salt + n) == challenge. Costs a browser about a second, which is
 * nothing for a person typing their details and a lot for a bot creating thousands of accounts.
 */
export type PowChallenge = {
  algorithm: string;
  challenge: string;
  maxNumber: number;
  salt: string;
  signature: string;
};

type Digest = (text: string) => Promise<string>;

async function sha256Hex(text: string): Promise<string> {
  const hash = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return Array.from(new Uint8Array(hash), (b) => b.toString(16).padStart(2, "0")).join("");
}

/** Hashes in batches (WebCrypto runs off the main thread) and yields between them. */
export async function solveChallenge(
  challenge: PowChallenge,
  digest: Digest = sha256Hex,
  batch = 500,
): Promise<number | null> {
  for (let start = 0; start <= challenge.maxNumber; start += batch) {
    const end = Math.min(start + batch, challenge.maxNumber + 1);
    const numbers = Array.from({ length: end - start }, (_, i) => start + i);
    const hashes = await Promise.all(numbers.map((n) => digest(challenge.salt + n)));
    const index = hashes.indexOf(challenge.challenge);
    if (index >= 0) return numbers[index]!;
    await new Promise((resolve) => setTimeout(resolve, 0));
  }
  return null;
}

/** The value of the X-Blanqo-Pow header for a solved challenge. */
export function encodeSolution(challenge: PowChallenge, number: number): string {
  const { challenge: hash, salt, signature } = challenge;
  return encodeURIComponent(JSON.stringify({ challenge: hash, salt, signature, number }));
}

/** When the challenge stops being accepted (ms), from the `expires` in its salt. */
export function expiresAt(challenge: PowChallenge): number {
  return Number(/[?&]expires=(\d+)/.exec(challenge.salt)?.[1] ?? 0) * 1000;
}

type Pending = { solution: Promise<string>; expires: number };
let pending: Pending | null = null;

function startSolving(): Pending {
  const entry: Pending = { solution: Promise.resolve(""), expires: Infinity };
  entry.solution = pb
    .send<PowChallenge>("/api/blanqo/pow/challenge", { method: "GET" })
    .then(async (challenge) => {
      entry.expires = expiresAt(challenge);
      const number = await solveChallenge(challenge);
      if (number === null) throw new Error("Could not solve the security check");
      return encodeSolution(challenge, number);
    });
  // A failed attempt is retried by the next take.
  entry.solution.catch(() => {
    if (pending === entry) pending = null;
  });
  return entry;
}

/** Starts solving in the background (the registration form calls this when it opens). */
export function prefetchPowSolution(): void {
  if (!pending || pending.expires - Date.now() < 60_000) pending = startSolving();
}

/** A fresh solution for one sign-up; each one is accepted once, so it is handed out once. */
export async function takePowSolution(): Promise<string> {
  prefetchPowSolution();
  const entry = pending!;
  pending = null;
  return entry.solution;
}
